// hotkeys/HotkeysProvider.tsx
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { Hotkey, HotkeyRegistration, HotkeyScope } from "./types";
import { shouldIgnoreKeyEvent } from "./isTyping";

type RegistryItem = Hotkey & { id: string };

type HotkeysContextType = {
  register: (hotkey: Hotkey) => HotkeyRegistration;
  setScope: (scope: HotkeyScope) => void;
  pushScope: (scope: HotkeyScope) => () => void;
  suppress: () => () => void;
  list: () => RegistryItem[];
  activeScope: HotkeyScope;
  isHelpOpen: boolean;
  setHelpOpen: (open: boolean) => void;
};

const HotkeysContext = createContext<HotkeysContextType | null>(null);

const makeId = (() => {
  let n = 0;
  return () => `hk_${++n}`;
})();

const parseCombo = (combo: string): string[] => combo.trim().split(/\s+/); // "g p" -> ["g","p"]

// Map pathname segments to scopes
const getRouteScope = (pathname: string): HotkeyScope => {
  const segment = pathname.split("/").filter(Boolean)[0];
  
  switch (segment) {
    case "dashboard": return "dashboard";
    case "patient": return "patient";
    case "receipt": return "receipt";
    case "terminplan": return "terminplan";
    case "transactions": return "transactions";
    case "admin": return "admin";
    case "termin": return "termin";
    // The MobiliCalendar preview IS the Termin surface — same scope so its
    // bridge-registered calendar hotkeys (see useMobiliCalendarHotkeys) fire.
    case "mobilical": return "termin";
    case "continuous": return "continuous";
    case "history": return "history";
    case "biDashboard": return "biDashboard";
    case "sales": return "sales";
    case "doctor-management": return "doctor-management";
    case "login": 
    case "reset-password": return "login";
    default: return "global";
  }
};

export const HotkeysProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [registry, setRegistry] = useState<RegistryItem[]>([]);
  const [suppressed, setSuppressed] = useState(false);
  const [isHelpOpen, setHelpOpen] = useState(false);
  const scopeStack = useRef<HotkeyScope[]>(["global"]);
  const [activeScope, setActiveScope] = useState<HotkeyScope>("global");

  // Update scope on route change
  const location = useLocation();
  useEffect(() => {
    const routeScope = getRouteScope(location.pathname);
    // Only update if we're not in a pushed scope
    if (scopeStack.current.length === 1) {
      scopeStack.current = [routeScope];
      setActiveScope(routeScope);
    }
  }, [location.pathname]);

  const register = useCallback((hotkey: Hotkey): HotkeyRegistration => {
    const id = makeId();
    const item: RegistryItem = { 
      id, 
      priority: 0, 
      scope: "global", 
      ...hotkey 
    };
    setRegistry(prev => [...prev, item]);
    
    return {
      id,
      unregister: () => setRegistry(prev => prev.filter(x => x.id !== id)),
    };
  }, []);

  const setScope = useCallback((scope: HotkeyScope) => {
    scopeStack.current = [scope];
    setActiveScope(scope);
  }, []);

  const pushScope = useCallback((scope: HotkeyScope) => {
    scopeStack.current = [...scopeStack.current, scope];
    setActiveScope(scope);
    return () => {
      scopeStack.current = scopeStack.current.filter(s => s !== scope);
      setActiveScope(scopeStack.current[scopeStack.current.length - 1] ?? "global");
    };
  }, []);

  const suppress = useCallback(() => {
    setSuppressed(true);
    return () => setSuppressed(false);
  }, []);

  const list = useCallback(() => registry, [registry]);

  // Sequence buffer for multi-key combinations
  const buffer = useRef<string[]>([]);
  const timer = useRef<number | null>(null);
  const SEQ_TIMEOUT = 600; // ms

  const clearBuffer = useCallback(() => {
    if (timer.current) window.clearTimeout(timer.current);
    timer.current = null;
    buffer.current = [];
  }, []);

  // Register global help shortcut
  useEffect(() => {
    const helpHotkey = register({
      combo: "?",
      scope: "global",
      description: "Tastaturkürzel anzeigen",
      handler: () => setHelpOpen(true),
      priority: 100,
    });
    
    return () => helpHotkey.unregister();
  }, [register]);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      // console.debug(`[Hotkeys] Key pressed: ${e.key}, suppressed: ${suppressed}`);
      
      if (shouldIgnoreKeyEvent(e, suppressed)) {
        // console.debug(`[Hotkeys] Key ignored due to shouldIgnoreKeyEvent`);
        return;
      }

      // Navigation keys should clear the buffer and not be processed as hotkeys
      const navigationKeys = [
        'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight',
        'PageUp', 'PageDown', 'Home', 'End',
        'Tab', 'Enter', 'Escape'
      ];
      
      if (navigationKeys.includes(e.key)) {
        // console.debug(`[Hotkeys] Navigation key detected (${e.key}), clearing buffer`);
        clearBuffer();
        return;
      }

      // Single characters are lowercased so caps-lock can't break combos —
      // EXCEPT when Shift is physically held: that keeps uppercase combos like
      // "J" (Shift+j) bindable and distinct from "j". Shifted symbols ("?")
      // come through as their own character either way.
      const key = e.key.length === 1 && !e.shiftKey ? e.key.toLowerCase() : e.key;
      buffer.current.push(key);
      // console.debug(`[Hotkeys] Buffer updated:`, buffer.current);

      if (timer.current) window.clearTimeout(timer.current);
      timer.current = window.setTimeout(clearBuffer, SEQ_TIMEOUT);

      // Find best match among active scopes (top of stack first), fall back to "global"
      const scopes = [...scopeStack.current].reverse();
      if (!scopes.includes("global")) {
        scopes.push("global");
      }
      // console.debug(`[Hotkeys] Active scopes:`, scopes);

      const candidates = registry.filter(h => {
        const hotkeyScope = (h.scope ?? "global") as HotkeyScope;
        return scopes.includes(hotkeyScope) && (h.when ? h.when() : true);
      });

      // console.debug(`[Hotkeys] Found ${candidates.length} candidates:`, candidates.map(c => ({ combo: c.combo, scope: c.scope, description: c.description })));

      // Match combos against the END of the buffer, token-by-token (no string
      // suffix tricks — "F1" must never match a "1" combo). The LONGEST
      // matching sequence wins, then priority: a "g a" navigation sequence
      // must beat a single-key "a" binding, regardless of their priorities.
      const bufTokens = buffer.current;
      const endsWithSeq = (seq: string[]) =>
        seq.length <= bufTokens.length &&
        seq.every((k, i) => bufTokens[bufTokens.length - seq.length + i] === k);

      const matched = candidates
        .map(h => ({ h, seq: parseCombo(h.combo) }))
        .filter(x => endsWithSeq(x.seq))
        .sort((a, b) =>
          b.seq.length - a.seq.length ||
          (b.h.priority ?? 0) - (a.h.priority ?? 0)
        )[0]?.h;

      if (matched) {
        // console.debug(`[Hotkeys] MATCH FOUND! Executing: ${matched.combo} (${matched.description || 'no description'})`);
        e.preventDefault();
        e.stopPropagation();
        matched.handler(e);
        clearBuffer();
        
        // Log telemetry (optional - can be removed in production)
        // console.debug(`Hotkey fired: ${matched.combo} (${matched.description || 'no description'})`);
      } else {
        // console.debug(`[Hotkeys] No match found for buffer: "${bufStr}"`);
      }
    };

    window.addEventListener("keydown", onKeyDown, { capture: true });
    return () => window.removeEventListener("keydown", onKeyDown, { capture: true });
  }, [registry, suppressed, clearBuffer]);

  const value = useMemo<HotkeysContextType>(() => ({ 
    register, 
    setScope, 
    pushScope, 
    suppress, 
    list, 
    activeScope,
    isHelpOpen,
    setHelpOpen
  }), [
    register, 
    setScope, 
    pushScope, 
    suppress, 
    list, 
    activeScope,
    isHelpOpen
  ]);

  return <HotkeysContext.Provider value={value}>{children}</HotkeysContext.Provider>;
};

export const useHotkeys = (): HotkeysContextType => {
  const ctx = useContext(HotkeysContext);
  if (!ctx) throw new Error("useHotkeys must be used within HotkeysProvider");
  return ctx;
};